/**
 * SignatureFlow — DocuSign-style envelope viewer.
 *
 * Shows envelope status, signers + their signing status, and a "Sign now"
 * affordance that opens a modal with a signature pad mock.
 */
import React, { useState, useRef, useEffect } from 'react';
import { CheckCircle2, Clock, FileSignature, X, PenTool, Mail } from 'lucide-react';

const formatDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

const SignaturePad = ({ onSign, onCancel, signerName }) => {
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [hasInk, setHasInk] = useState(false);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    ctx.strokeStyle = '#1e3a8a';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
  }, []);

  const getPos = (e) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const t = e.touches?.[0];
    return { x: (t?.clientX ?? e.clientX) - rect.left, y: (t?.clientY ?? e.clientY) - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    setDrawing(true);
    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const move = (e) => {
    if (!drawing) return;
    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasInk(true);
  };
  const end = () => setDrawing(false);

  const clear = () => {
    const c = canvasRef.current;
    c.getContext('2d').clearRect(0, 0, c.width, c.height);
    setHasInk(false);
  };

  return (
    <div className="sig-modal-overlay" onClick={onCancel}>
      <div className="sig-modal" onClick={(e) => e.stopPropagation()}>
        <div className="sig-modal-head">
          <div>
            <h3>Sign document</h3>
            <p>{signerName} — draw your signature below</p>
          </div>
          <button className="sig-modal-close" onClick={onCancel}><X size={18} /></button>
        </div>
        <div className="sig-canvas-wrap">
          <canvas
            ref={canvasRef}
            width={520}
            height={180}
            className="sig-canvas"
            onMouseDown={start}
            onMouseMove={move}
            onMouseUp={end}
            onMouseLeave={end}
            onTouchStart={start}
            onTouchMove={move}
            onTouchEnd={end}
          />
          <div className="sig-canvas-hint">×</div>
          <div className="sig-canvas-line"></div>
        </div>
        <div className="sig-modal-actions">
          <button className="manifest-btn-ghost" onClick={clear}>Clear</button>
          <button
            className="manifest-btn-primary-sm"
            onClick={onSign}
            disabled={!hasInk}
          >
            <PenTool size={13} /> Apply signature
          </button>
        </div>
      </div>
    </div>
  );
};

const SignatureFlow = ({ envelope, onSign }) => {
  const [padOpen, setPadOpen] = useState(false);
  const pendingMe = envelope.signers.find((s) => s.status === 'pending' && s.role.toLowerCase().includes('you'));

  return (
    <div className="sig-envelope">
      <div className="sig-envelope-head">
        <div className="sig-envelope-icon"><FileSignature size={18} /></div>
        <div className="sig-envelope-main">
          <div className="sig-envelope-title">{envelope.title}</div>
          <div className="sig-envelope-doc">{envelope.docName}</div>
        </div>
        <div className={`sig-envelope-status status-${envelope.status}`}>
          {envelope.status === 'completed' ? <CheckCircle2 size={13} /> : <Clock size={13} />}
          {envelope.status === 'completed' ? 'Completed' : 'Pending signatures'}
        </div>
      </div>

      <div className="sig-envelope-body">
        <div className="sig-envelope-meta">
          <span><strong>Sent:</strong> {formatDate(envelope.sentAt)}</span>
          {envelope.completedAt && <span><strong>Completed:</strong> {formatDate(envelope.completedAt)}</span>}
        </div>

        <ul className="sig-signers">
          {envelope.signers.map((s, i) => (
            <li key={i} className={`sig-signer status-${s.status}`}>
              <div className={`sig-signer-marker status-${s.status}`}>
                {s.status === 'signed' ? <CheckCircle2 size={14} /> : <Clock size={14} />}
              </div>
              <div className="sig-signer-main">
                <div className="sig-signer-name">{s.name}</div>
                <div className="sig-signer-role">{s.role}</div>
                <div className="sig-signer-email"><Mail size={10} /> {s.email}</div>
              </div>
              <div className="sig-signer-stat">
                {s.status === 'signed'
                  ? `Signed ${formatDate(s.signedAt)}`
                  : 'Awaiting signature'}
              </div>
            </li>
          ))}
        </ul>

        {pendingMe && (
          <div className="sig-cta">
            <button className="manifest-btn-primary-sm" onClick={() => setPadOpen(true)}>
              <PenTool size={13} /> Sign now
            </button>
          </div>
        )}
      </div>

      {padOpen && (
        <SignaturePad
          signerName={pendingMe?.name}
          onCancel={() => setPadOpen(false)}
          onSign={() => {
            setPadOpen(false);
            if (onSign) onSign(envelope.id);
          }}
        />
      )}
    </div>
  );
};

export default SignatureFlow;
